import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Lock } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { Switch } from '@/src/renderer/components/ui/switch'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { getPlatformInfo } from '@/src/renderer/lib/platform'
import { useBackupBridge, type AutoBackupEntry, type AutoBackupStatus, type OpenedBackup } from '@/src/renderer/lib/backup/backupBridge'
import { groupAutoBackups } from '@/src/renderer/lib/backup/autoBackup'
import { SettingsRow, SettingsSection } from '../../primitives'
import './AutoBackupSection.scss'

type Notice = 'moveFailed' | 'notWritable' | 'missing' | null

interface AutoBackupSectionProps {
  /** Ein Backup aus der Liste wiederherstellen — die Seite öffnet dafür die Unterseite. */
  onRestore: (file: OpenedBackup) => void
}

const formatSize = (bytes: number, language: string) => {
  const megabytes = bytes >= 1_000_000
  const value = megabytes ? bytes / 1_000_000 : Math.max(1, bytes / 1_000)
  return `${new Intl.NumberFormat(language, { maximumFractionDigits: megabytes ? 1 : 0 }).format(value)} ${megabytes ? 'MB' : 'KB'}`
}

const formatDate = (key: string, language: string) => {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * **Backups › Automatische Backups** (#158, Entscheidung 4 und 19): Schalter, Ordner und die letzten
 * Backups — erst die eigenen (diese Woche, ältere), darunter die anderer Geräte im selben Ordner. Aus der
 * Liste geht es direkt ins Wiederherstellen; ein neuer Rechner nimmt so das Backup des alten.
 *
 * Geschrieben wird im Hauptfenster (`useAutoBackup`); die Liste holt sich beim Zurückkommen ins Fenster
 * den neuen Stand.
 */
export function AutoBackupSection({ onRestore }: AutoBackupSectionProps) {
  const { t, i18n } = useTranslation()
  const bridge = useBackupBridge()
  const [status, setStatus] = useState<AutoBackupStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<Notice>(null)

  const refresh = useCallback(() => bridge.autoStatus().then(setStatus).catch(error => {
    console.error('[Backup] Stand der automatischen Backups nicht lesbar:', error)
  }), [bridge])

  useEffect(() => {
    void refresh()
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refresh])

  if (!status) return null

  const onToggle = (enabled: boolean) => {
    setStatus({ ...status, enabled })
    void bridge.autoSetEnabled(enabled).then(setStatus).catch(error => console.error('[Backup] Schalter nicht gespeichert:', error))
  }

  const onChooseFolder = async () => {
    setBusy(true)
    setNotice(null)
    try {
      const result = await bridge.chooseFolder()
      setStatus(result.status)
      if (result.changed && result.moveFailed) setNotice('moveFailed')
      if (!result.changed && result.notWritable) setNotice('notWritable')
    } catch (error) {
      console.error('[Backup] Ordnerwahl gescheitert:', error)
    } finally {
      setBusy(false)
    }
  }

  const onRestoreEntry = async (entry: AutoBackupEntry) => {
    setBusy(true)
    setNotice(null)
    try {
      const file = await bridge.autoRead(entry)
      if (file) {
        onRestore(file)
      } else {
        setNotice('missing')
        await refresh()
      }
    } catch (error) {
      console.error('[Backup] Backup aus der Liste nicht lesbar:', error)
      setNotice('missing')
    } finally {
      setBusy(false)
    }
  }

  const groups = groupAutoBackups(status.backups)
  const lists = [
    { key: 'recent', title: t('preferences.backups.auto.groupRecent'), entries: groups.recent },
    { key: 'older', title: t('preferences.backups.auto.groupOlder'), entries: groups.older },
    { key: 'otherDevices', title: t('preferences.backups.auto.groupOtherDevices'), entries: groups.otherDevices },
  ].filter(list => list.entries.length > 0)

  const meta = (entry: AutoBackupEntry) => [
    entry.own ? null : entry.deviceName,
    entry.location === 'app' && !status.isDefaultFolder ? t('preferences.backups.auto.inAppFolder') : null,
    formatSize(entry.size, i18n.language),
  ].filter(Boolean).join(' · ')

  const folderNotices = [
    !status.reachable && t('preferences.backups.auto.unreachable'),
    (notice === 'moveFailed' || notice === 'notWritable') && t(`preferences.backups.auto.${notice}`),
  ].filter((text): text is string => Boolean(text))

  return (
    <>
      <SettingsSection title={t('preferences.backups.auto.title')} variant="card">
        <SettingsRow
          label={t('preferences.backups.auto.toggle')}
          description={t('preferences.backups.auto.description')}
          htmlFor="backups-auto-enabled"
          control={<Switch id="backups-auto-enabled" checked={status.enabled} onCheckedChange={onToggle} />}
        />
        <SettingsRow
          label={t('preferences.backups.auto.folder')}
          description={(
            <>
              <WithTooltip content={status.folder}>
                <span className="auto-backups__path">{status.folder}</span>
              </WithTooltip>
              <span role="status" aria-live="polite" className="auto-backups__notices">
                {folderNotices.map(text => <Notice key={text} text={text} />)}
              </span>
            </>
          )}
          control={(
            <div className="auto-backups__folder-actions">
              <Button variant="outline" disabled={busy} onClick={() => void onChooseFolder()}>
                {t('preferences.backups.auto.change')}
              </Button>
              <Button variant="secondary" onClick={() => void bridge.reveal()}>
                {t(getPlatformInfo().isMac ? 'preferences.backups.auto.revealMac' : 'preferences.backups.auto.revealWin')}
              </Button>
            </div>
          )}
        />
      </SettingsSection>

      <SettingsSection title={t('preferences.backups.auto.list')} variant="card" className="auto-backups">
        <div role="status" aria-live="polite" className="auto-backups__notices">
          {notice === 'missing' && <Notice text={t('preferences.backups.auto.missing')} />}
        </div>
        {lists.length === 0 && <p className="auto-backups__empty">{t('preferences.backups.auto.empty')}</p>}
        {lists.map(list => (
          <section key={list.key} className="auto-backups__group" aria-label={list.title}>
            <h3 className="auto-backups__group-title">{list.title}</h3>
            <ul className="auto-backups__entries">
              {list.entries.map(entry => {
                const date = formatDate(entry.date, i18n.language)
                return (
                  <li key={`${entry.location}/${entry.name}`} className="auto-backups__entry">
                    <SettingsRow
                      label={date}
                      description={(
                        <span className="auto-backups__meta">
                          {entry.encrypted && (
                            <Lock size={12} role="img" aria-label={t('preferences.backups.password.protected')} className="auto-backups__lock" />
                          )}
                          {meta(entry)}
                        </span>
                      )}
                      control={(
                        <Button
                          variant="outline"
                          disabled={busy}
                          aria-label={t('preferences.backups.auto.restoreEntry', { date })}
                          onClick={() => void onRestoreEntry(entry)}
                        >
                          {t('preferences.backups.auto.restore')}
                        </Button>
                      )}
                    />
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </SettingsSection>
    </>
  )
}

function Notice({ text }: { text: string }) {
  return (
    <span className="auto-backups__notice">
      <CircleAlert size={14} aria-hidden="true" />
      {text}
    </span>
  )
}
